import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "../entities/User";
import { SavedItem } from "../entities/SavedItem";
import { Order } from "../entities/Order";
import { Product } from "../entities/Product";
import { Button } from "@/components/ui/button.jsx";
import { motion } from "framer-motion";
import AccountInfo from "../components/account/AccountInfo";
import SavedItems from "../components/account/SavedItems";
import OrderHistory from "../components/account/OrderHistory";

export default function AccountPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("saved");
  const [savedItems, setSavedItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const currentUser = await User.me();
      setUser(currentUser);
      
      // Fixed: Better error handling for saved items
      const saved = await SavedItem.list();
      const productPromises = saved.map(async (item) => {
        try {
          const prods = await Product.filter({ id: item.product_id });
          return prods[0] || null;
        } catch (err) {
          console.error(`Error loading product ${item.product_id}:`, err);
          return null;
        }
      });
      
      const products = await Promise.all(productPromises);
      // Fixed: Filter out null values from failed product fetches
      setSavedItems(products.filter(p => p !== null));
      
      const userOrders = await Order.filter({ created_by: currentUser.email }, "-order_date");
      setOrders(userOrders);
    } catch (error) {
      console.error("Error loading account data:", error);
      setError('Failed to load account data');
      // Fixed: Redirect to home if not logged in
      if (error.message?.includes('not authenticated')) {
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--brand)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Fixed: Better error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="panel p-8 text-center">
          <p className="text-[var(--muted)] mb-6">{error}</p>
          <Button 
            onClick={loadAccountData}
            className="btn-outline px-5 py-2.5"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <div className="page-wrap">
        <div className="panel grid lg:grid-cols-5 overflow-hidden">
          <div className="lg:col-span-2 p-8 md:p-10 bg-[var(--accent-soft)]/50 border-b lg:border-b-0 lg:border-r border-[var(--line)]">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
              <p className="chip mb-4">My Account</p>
            <h1 className="text-4xl md:text-5xl mb-5 leading-tight">
              Hi, {user?.full_name?.split(' ')[0] || 'User'}
            </h1>
              <p className="text-[var(--muted)] mb-8">
              You can manage your account here. Please, choose what you'd like to do.
            </p>

            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab("saved")}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                  activeTab === "saved"
                      ? 'bg-[var(--accent-soft)] text-[var(--ink)] shadow-sm'
                      : 'text-[var(--muted)] hover:bg-[var(--accent-soft)]'
                }`}
              >
                SAVED ITEMS
              </button>
              <button
                onClick={() => setActiveTab("contact")}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                  activeTab === "contact"
                      ? 'bg-[var(--accent-soft)] text-[var(--ink)] shadow-sm'
                      : 'text-[var(--muted)] hover:bg-[var(--accent-soft)]'
                }`}
              >
                CONTACT INFORMATION
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                  activeTab === "orders"
                      ? 'bg-[var(--accent-soft)] text-[var(--ink)] shadow-sm'
                      : 'text-[var(--muted)] hover:bg-[var(--accent-soft)]'
                }`}
              >
                ORDER HISTORY
              </button>
            </nav>
          </motion.div>
        </div>

          <div className="lg:col-span-3 p-8 md:p-10">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {activeTab === "saved" && <SavedItems items={savedItems} onUpdate={loadAccountData} />}
            {activeTab === "contact" && <AccountInfo user={user} onUpdate={loadAccountData} />}
            {activeTab === "orders" && <OrderHistory orders={orders} />}
          </motion.div>
        </div>
      </div>
    </div>
    </div>
  );
}